import React, { useEffect, Fragment, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { Card, CardBody, Row, Col, Container, FormGroup, Button, UncontrolledTooltip } from "reactstrap";
import { getInvoiceById } from "./store/citygoddessPurchaseItemSlice";
import { exportInvoiceDetailReport, handleXeroUpload } from "./store/citygoddessPurchaseReportSlice";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import Breadcrumb from "../../../layout/breadcrumb";
import { toast } from "react-toastify";


const PurchaseInvoiceView = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("All");
    const [exportLoading, setExportLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const { selectedInvoice, invoiceItems, totalItems, viewLoading } = useSelector(
        ({ report }) => report.purchaseInvoiceItemReport
    );

    useEffect(() => {
        if (!id) return;
        dispatch(getInvoiceById({ invoiceId: id, page, pageSize, search, type: typeFilter }));
    }, [id, page, pageSize, search, typeFilter, dispatch]);

    const exportRow = () => {
        if (exportLoading) return;
        setExportLoading(true);
        dispatch(exportInvoiceDetailReport(id)).then((res) => {
            const link = document.createElement("a");
            link.href = res.payload.response;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }).finally(() => {
            setTimeout(() => setExportLoading(false), 1500);
        });
    };

    const handleUpload = () => {
        setIsUploading(true);
        dispatch(handleXeroUpload(id)).then((res) => {
            setIsUploading(false);
            if (res.payload.response.code === '200') {
                toast.success("Invoice uploaded to Xero successfully");
            } else {
                toast.error("Failed to upload invoice to Xero");
            }
        }).catch(() => {
            setIsUploading(false);
            toast.error("Error uploading invoice");
        });
    };

    const handleSearchChange = (value) => {
        setSearch(value);
        setPage(1);
    };

    const handleTypeChange = (value) => {
        setTypeFilter(value);
        setPage(1);
    };

    const numberFormat = (val) => {
        return Number(val || 0).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatNumberOnly = (value) => {
        const num = Number(value || 0);

        return num.toLocaleString("en-US", {
            minimumFractionDigits: num % 1 === 0 ? 0 : 2,
            maximumFractionDigits: 2,
        });
    };

    const getOrderLink = (row) => {
        return `${process.env.PUBLIC_URL}/order/${row.order?.id}/view`;
    };

    const columns = [
         {
            name: <span title="Order Number">Order Number</span>,
            selector: row => row.order?.order_name,
            cell: row => (
                <a
                    href={getOrderLink(row)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={row.order?.order_name}
                    style={{ color: '#007bff' }}
                >
                    {row.order?.order_name}
                </a>
            ),
            sortable: true,
            left: true,
        },
        {
            name: <span title="Type">Type</span>,
            selector: row => row.type,
            cell: row => <span title={row.type}>{row.type}</span>,
            sortable: true,
            left: true,
        },
        {
            name: <span title="Processed Date">Processed Date</span>,
            selector: row => row.processed_date,
            sortable: true,
        },
        {
            name: <span title="Stock Keeping Unit">SKU</span>,
            selector: row => row.product_variant?.sku,
            cell: row => <span title={row.product_variant?.sku}>{row.product_variant?.sku}</span>,
            sortable: true,
            left: true,
            grow: 1
        },
        {
            name: <span title="Quantity">Quantity</span>,
            selector: row => formatNumberOnly(row.quantity),
            sortable: true,
            right: true
        },
        {
            name: <span title="Unit Cost">Unit Cost</span>,
            selector: row => numberFormat(row.unit_cost),
            sortable: true,
            right: true
        },
        {
            name: <span title="Cost">Cost</span>,
            selector: row => numberFormat(row.cost),
            sortable: true,
            right: true
        },
        {
            name: <span title="Tax">Tax (₹)</span>,
            selector: row => numberFormat(row.tax),
            sortable: true,
            right: true
        },
        {
            name: <span title="Net Cost">Net Cost (₹)</span>,
            selector: row => numberFormat(row.cost_with_tax),
            sortable: true,
            right: true
        },
    ];

    const dataMenu = [
        {
            type: 1,
            text: 'Purchase Invoices - Citygoddess',
            link: '/report/citygoddess/purchase-report'
        },
        {
            type: 0,
            text: 'View Purchase Invoice Details',
            link: ''
        },
    ];

    return (
        <Fragment>
            <Breadcrumb title="Purchase Invoices Items - Citygoddess" data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        {selectedInvoice && (
                            <Card className="mb-4">
                                <CardBody>
                                    <div className="d-flex justify-content-end mb-3">
                                        <Button
                                            id="backBtn"
                                            size="sm"
                                            color="secondary"
                                            className="mr-2"
                                            onClick={() => history.push('/report/citygoddess/purchase-report')}
                                        >
                                            <i className="fa fa-arrow-left mr-1"></i> Back
                                        </Button>
                                        <UncontrolledTooltip placement="top" target="backBtn">Back to Purchase Invoices</UncontrolledTooltip>

                                        <Button
                                            id="syncXeroBtn"
                                            size="sm"
                                            color="warning"
                                            className="mr-2"
                                            disabled={isUploading}
                                            onClick={handleUpload}
                                        >
                                            {isUploading ? <i className="fa fa-spinner fa-spin mr-1"></i> : <i className="fa fa-refresh mr-1"></i>}
                                            Sync to Xero
                                        </Button>
                                        <UncontrolledTooltip placement="top" target="syncXeroBtn">Upload Invoice to Xero</UncontrolledTooltip>

                                        <Button
                                            id="exportBtn"
                                            size="sm"
                                            color="primary"
                                            disabled={exportLoading}
                                            onClick={exportRow}
                                        >
                                            {exportLoading ? <i className="fa fa-spinner fa-spin mr-1"></i> : <i className="fa fa-download mr-1"></i>}
                                            Export
                                        </Button>
                                        <UncontrolledTooltip placement="top" target="exportBtn">Export Invoice Details</UncontrolledTooltip>
                                    </div>
                                    <Row>
                                        <Col md="4" xl="4" sm="12"><b>Invoice Date:</b> {selectedInvoice.invoice_date}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Order Dates:</b> {selectedInvoice.order_start_date} - {selectedInvoice.order_end_date}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Return Dates:</b> {selectedInvoice.return_start_date} - {selectedInvoice.return_end_date}</Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md="4" xl="4" sm="12"><b>Ordered Qty:</b> {formatNumberOnly(selectedInvoice.total_ordered_qty)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Returned Qty:</b> {formatNumberOnly(selectedInvoice.total_returned_qty)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Net Qty:</b> {formatNumberOnly(selectedInvoice.total_net_qty)}</Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md="4" xl="4" sm="12"><b>Total Cost:</b> ₹ {numberFormat(selectedInvoice.total_cost)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Tax:</b> ₹ {numberFormat(selectedInvoice.total_tax)}</Col>
                                        <Col md="4" xl="4" sm="12"><b>Net Amount:</b> ₹ {numberFormat(selectedInvoice.total_cost_with_tax)}</Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        )}

                        <Row className="mb-3">
                            <Col xl="6" sm="12">
                                <FormGroup className="mb-0">
                                    <input
                                        type="text"
                                        placeholder="Search by SKU ..."
                                        className="form-control"
                                        value={search}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                    />
                                </FormGroup>
                            </Col>
                            <Col xl="6" sm="12">
                                <FormGroup className="mb-0">
                                    <select
                                        className="form-control"
                                        value={typeFilter}
                                        onChange={(e) => handleTypeChange(e.target.value)}
                                    >
                                        <option value="All">All</option>
                                        <option value="Order">Order</option>
                                        <option value="Return">Return</option>
                                    </select>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Card>
                            <CardBody>
                                <div className="table-responsive product-table">
                                    <CommonDataTable
                                        headerColumns={columns}
                                        gridData={invoiceItems}
                                        noAction
                                        fixedHeader
                                        fixedHeaderScrollHeight="600px"
                                        progressPending={viewLoading}
                                        paginationServer
                                        paginationTotalRows={totalItems}
                                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                        onChangePage={(p) => setPage(p)}
                                        onChangeRowsPerPage={(size) => { setPageSize(size); setPage(1); }}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </Container>
        </Fragment>
    );
};

export default PurchaseInvoiceView;
